import { bind } from 'bind-event-listener';

import type { getPosHandlerNode } from '@atlaskit/editor-common/types';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

const NO_SELECT_ATTR_SELECTOR = '[data-no-extension-select]';

export interface NodeViewDom {
	/** The element React content is portalled into. Carries the
	 * canonical `extension-container` class so floating-toolbar
	 * anchoring (`findDomRefAtPos`) resolves to it. */
	mountPoint: HTMLDivElement;
	/** The outer wrapper appended to the editor by ProseMirror. */
	root: HTMLDivElement;
}

/**
 * Builds the static DOM scaffold for the agent-managed-extension
 * nodeView. Pure — only touches the supplied `doc`.
 */
export const createNodeViewDom = (doc: Document, node: PmNode): NodeViewDom => {
	const root = doc.createElement('div');
	root.classList.add('agent-managed-extension');
	root.setAttribute('data-node-type', 'bodied-extension');
	root.setAttribute('data-extension-type', node.attrs.extensionType);
	root.setAttribute('data-extension-key', node.attrs.extensionKey);
	root.setAttribute('data-local-id', node.attrs.localId ?? '');

	const mountPoint = doc.createElement('div');
	mountPoint.classList.add('extension-container');
	root.appendChild(mountPoint);

	return { root, mountPoint };
};

/**
 * Wires a `mousedown` handler on `dom` that turns body clicks into a
 * `NodeSelection` of the bodied extension at `getPos()`. Returns a
 * cleanup function. Clicks on `[data-no-extension-select]` descendants
 * (e.g. popup buttons) are ignored so chrome controls keep working.
 */
export const attachBodyClickSelection = (
	view: EditorView,
	dom: HTMLElement,
	getPos: getPosHandlerNode,
): (() => void) => {
	const handle = (e: MouseEvent) => {
		const pos = getPos();
		if (typeof pos !== 'number') {
			return;
		}
		const target = e.target as HTMLElement | null;
		if (target?.closest(NO_SELECT_ATTR_SELECTOR)) {
			return;
		}
		e.preventDefault();
		view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
	};
	return bind(dom, { type: 'mousedown', listener: handle });
};
